import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, FacebookIcon, InstagramIcon, SendIcon, YoutubeIcon } from 'lucide-react';
import { ObfuscatedEmail } from '../ui/ObfuscatedEmail';
type FooterLink = {
  key: string;
  source: string;
  fallback: string;
  to: string;
};
const quickLinks: FooterLink[] = [{
  key: 'footer.quickLinks.aboutUs',
  source: 'About Us',
  fallback: 'من نحن',
  to: '/ar/about'
}, {
  key: 'footer.quickLinks.forChildren',
  source: 'For Children',
  fallback: 'للأطفال',
  to: '/ar/children'
}, {
  key: 'footer.quickLinks.forParents',
  source: 'For Parents',
  fallback: 'للآباء والأمهات',
  to: '/ar/parents'
}, {
  key: 'footer.quickLinks.forAdults',
  source: 'For Adults',
  fallback: 'للبالغين',
  to: '/ar/adults'
}, {
  key: 'footer.quickLinks.partnership',
  source: 'Partnership',
  fallback: 'الشراكات',
  to: '/ar/partnership'
}, {
  key: 'footer.quickLinks.contact',
  source: 'Contact',
  fallback: 'تواصل معنا',
  to: '/ar/contact'
}];
const legalLinks: FooterLink[] = [{
  key: 'footer.legal.terms',
  source: 'Terms & Conditions',
  fallback: 'الشروط والأحكام',
  to: '/ar/legal/terms-and-conditions'
}, {
  key: 'footer.legal.privacy',
  source: 'Privacy Policy',
  fallback: 'سياسة الخصوصية',
  to: '/ar/legal/privacy-policy'
}, {
  key: 'footer.legal.refund',
  source: 'Refund Policy',
  fallback: 'سياسة الاسترداد',
  to: '/ar/legal/refund-policy'
}, {
  key: 'footer.legal.safeguarding',
  source: 'Child Safeguarding Policy',
  fallback: 'سياسة حماية الطفل',
  to: '/ar/legal/child-safeguarding-policy'
}, {
  key: 'footer.legal.conduct',
  source: 'Code of Conduct',
  fallback: 'مدونة السلوك',
  to: '/ar/legal/code-of-conduct'
}, {
  key: 'footer.legal.complaints',
  source: 'Complaints & Reporting',
  fallback: 'الشكاوى والإبلاغ',
  to: '/ar/legal/complaints-and-reporting'
}];
export function ArabicFooter() {
  const [subscribed, setSubscribed] = useState(false);
  const translate = (_key: string, _source: string, arabicCopy: string) => arabicCopy;
  const handleNewsletter = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      await fetch('https://iilh.us5.list-manage.com/subscribe/post?u=436a7ad067b75120ed0ea7da1&id=7e4d2cd880&f_id=0048f2e0f0', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString()
      });
    } catch {
      // Mailchimp's no-cors response is opaque; show the submitted state consistently.
    }
    setSubscribed(true);
  };
  return <footer className="mt-auto bg-forest-700 text-ivory" dir="rtl" data-id="element-372" data-mp="1n5dk9eqytn52">
      <div className="mx-auto max-w-container px-5 py-14 md:px-8 md:py-16 lg:px-10" data-id="element-373" data-mp="1n6ooffobowwp">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8" data-id="element-374" data-mp="1idvmezk8o2ob">
          <div className="relative flex flex-col gap-4 pt-20 text-right sm:col-span-2 md:pt-24 lg:col-span-4" data-id="element-375" data-mp="3126q6sbvf0c3">
            <div className="absolute right-0 top-0 h-16 w-[238px] overflow-hidden md:h-20 md:w-[298px]" aria-hidden="true" data-id="element-376" data-mp="361b1ppm9pa5l">
              <img src="https://cdn.magicpatterns.com/uploads/jjmyAfsTVDBworZww9qTYu/iilh-horizontal-reverse.svg" alt="" className="h-16 max-w-none translate-x-[42px] select-none md:h-20 md:translate-x-[52px]" draggable={false} data-id="element-377" data-mp="3a0s8cmdfkdcc" />
            </div>
            <address className="w-full text-right not-italic text-[13px] leading-loose text-ivory/75" data-id="element-378" data-mp="3tq2mvx1fws8v">
              Aia tn 10، Tallinn،
              <br data-id="element-379" data-mp="1l8htv45990z" />
              10111 Estonia
              <br data-id="element-380" data-mp="1o4hbnvn45rj" />
              <br data-id="element-381" data-mp="6lmrtipb2t1n" />
              الهاتف:{' '}
              <a href="tel:+37258888275" className="transition hover:text-sand-200" dir="ltr" data-id="element-382" data-mp="6mxvzjmny2ta">
                +372 5888 8275
              </a>
              <br data-id="element-383" data-mp="3u34worvr0v48" />
              البريد الإلكتروني:{' '}
              <ObfuscatedEmail user="info" domain="iilh.org" className="transition hover:text-sand-200" data-id="element-384" data-mp="3u1k59b4qdsal" />
            </address>
          </div>

          <FooterLinkGroup title={translate('footer.quickLinks', 'Quick Links', 'روابط سريعة')} links={quickLinks} translate={translate} className="lg:col-span-2" data-id="element-385" data-mp="7msc1h02oxqa" />
          <FooterLinkGroup title={translate('footer.policiesLegal', 'Policies & Legal', 'السياسات والقوانين')} links={legalLinks} translate={translate} className="lg:col-span-3" data-id="element-386" data-mp="3b63q52mjqmy6" />

          <section className="sm:col-span-2 lg:col-span-3" aria-labelledby="arabic-newsletter-heading" data-id="element-387" data-mp="3g33wlenb5rpt">
            <h2 id="arabic-newsletter-heading" className="mb-4 text-[12px] font-semibold text-sand-200" data-id="element-388" data-mp="10b6b802we03f">
              {translate('footer.newsletter', 'Newsletter', 'النشرة البريدية')}
            </h2>
            <p className="mb-4 text-[13px] leading-relaxed text-ivory/80" data-id="element-389" data-mp="12vm5jwggxzq7">
              {translate('footer.newsletterDescription', 'Reminders and beneficial knowledge to your inbox', 'تصل إليك تذكيرات ومعرفة نافعة مباشرة إلى بريدك الإلكتروني')}
            </p>
            {subscribed ? <div className="flex items-center gap-2 rounded-full border border-ivory/15 bg-ivory/5 px-5 py-3 text-[13px] text-sand-200" data-id="element-390" data-mp="1ln58626kzbrp">
                <CheckIcon size={14} strokeWidth={2} aria-hidden="true" data-id="element-391" data-mp="1ql6pahkg510j" />
                {translate('footer.newsletterSuccess', 'Thanks for subscribing!', 'شكرًا لاشتراكك!')}
              </div> : <form onSubmit={handleNewsletter} className="relative" data-id="element-392" data-mp="1gv3xgkp29t3s">
                <label className="sr-only" htmlFor="arabic-footer-email" data-id="element-393" data-mp="24lhl7z0m55sk">
                  {translate('footer.email', 'Email', 'البريد الإلكتروني')}
                </label>
                <input id="arabic-footer-email" type="email" name="EMAIL" required placeholder={translate('footer.emailPlaceholder', 'Your Email', 'بريدك الإلكتروني')} className="w-full rounded-full border border-ivory/15 bg-ivory/10 px-5 py-3 pl-14 text-[13px] text-ivory placeholder:text-ivory/50 transition focus:border-ivory/30 focus:outline-none" data-id="element-394" data-mp="2j8pxl35nrh64" />
                <div className="hidden" aria-hidden="true" data-id="element-395" data-mp="20byil5x5fo7v">
                  <input type="text" name="b_436a7ad067b75120ed0ea7da1_7e4d2cd880" tabIndex={-1} defaultValue="" data-id="element-396" data-mp="1vi2ncb89sfge" />
                </div>
                <button type="submit" className="absolute left-1.5 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-sand-300 text-ink-900 transition hover:bg-sand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-ivory" aria-label={translate('footer.subscribe', 'Subscribe', 'اشترك')} data-id="element-397" data-mp="1mka4wy794ros">
                  <SendIcon size={16} strokeWidth={2} aria-hidden="true" data-id="element-398" data-mp="17h8rye9th70r" />
                </button>
              </form>}
          </section>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-ivory/10 pt-6 pb-8 text-center md:flex-row md:text-right" data-id="element-399" data-mp="1hcaed2cpjuzn">
          <p className="text-[12px] text-ivory/60" data-id="element-400" data-mp="ooodi1uv74gh">
            {translate('footer.copyright', '© 2026 MTÜ IILH (International Islamic Learning Hub)', '© 2026 MTÜ IILH (المركز الدولي للتعلّم الإسلامي)')}
          </p>
          <div className="flex items-center gap-2" aria-label="روابط التواصل الاجتماعي" data-id="element-401" data-mp="ygkdxjkfb6nv">
            <SocialLink href="https://www.facebook.com/iilhorg/" label="Facebook" data-id="element-402" data-mp="x98u2fm8b22">
              <FacebookIcon size={15} strokeWidth={1.5} data-id="element-403" data-mp="5wvloarj7pac" />
            </SocialLink>
            <SocialLink href="https://www.instagram.com/iilhorg/" label="Instagram" data-id="element-404" data-mp="1k5of70ylx5nq">
              <InstagramIcon size={15} strokeWidth={1.5} data-id="element-405" data-mp="1g438xygkvpmi" />
            </SocialLink>
            <SocialLink href="https://www.tiktok.com/@iilhorg" label="TikTok" data-id="element-406" data-mp="1u3m1vk0tiue8">
              <TikTokIcon data-id="element-407" data-mp="1p3zo99h2vc4w" />
            </SocialLink>
            <SocialLink href="https://www.youtube.com/@iilhorg" label="YouTube" data-id="element-408" data-mp="1p385x0chrawk">
              <YoutubeIcon size={15} strokeWidth={1.5} data-id="element-409" data-mp="232zgss7pob6c" />
            </SocialLink>
          </div>
        </div>
      </div>
    </footer>;
}
function FooterLinkGroup({
  title,
  links,
  translate,
  className
}: {
  title: string;
  links: FooterLink[];
  translate: (key: string, source: string, fallback: string) => string;
  className: string;
}) {
  return <section className={className} data-id="element-410" data-mp="27wtwzlo3y80o">
      <h2 className="mb-4 text-[12px] font-semibold text-sand-200" data-id="element-411" data-mp="2mqysc5a3nmct">{title}</h2>
      <ul className="space-y-2.5 text-[13px] text-ivory/80" data-id="element-412" data-mp="2hsx7pfx0zez8">
        {links.map(link => <li key={link.to} data-id="element-413" data-mp="31eqd9ziwn9rq">
            <Link to={link.to} className="transition hover:text-sand-200" data-id="element-414" data-mp="31fib7gzz5jl6">
              {translate(link.key, link.source, link.fallback)}
            </Link>
          </li>)}
      </ul>
    </section>;
}
function SocialLink({
  href,
  label,
  children
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ivory/15 text-ivory/80 transition hover:bg-ivory/10 hover:text-sand-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sand-200" aria-label={label} data-id="element-415" data-mp="2whgo8kzfh04f">
      {children}
    </a>;
}
function TikTokIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-416" data-mp="2dmiblqui158d">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" data-id="element-417" data-mp="kf3hm3usvfzj" />
    </svg>;
}